import client from './client';
import { geospatialApi } from './geospatialApi';
import { institutionApi } from './institutionsApi';
import {
  speciesApi,
  breedEcotypeApi,
  productionPurposeApi,
  ageClassApi,
} from './speciesLivestockRegistryApi';
import { facilityLookupsApi } from './facilitiesApi';
import { NIGERIAN_STATES } from '../component/Admin/data/enums';

const DEFAULT_PRODUCTION_SYSTEMS = [
  { value: 'pastoral', label: 'Pastoral' },
  { value: 'agropastoral', label: 'Agropastoral' },
  { value: 'commercial', label: 'Commercial' },
  { value: 'backyard', label: 'Backyard' },
  { value: 'nomadic', label: 'Nomadic' },
  { value: 'extensive', label: 'Extensive' },
];

const DEFAULT_COUNT_METHODS = [
  { value: 'physical_count', label: 'Physical Count' },
  { value: 'naits_registry', label: 'NAITS Registry' },
  { value: 'lga_records', label: 'LGA Records' },
  { value: 'estimate', label: 'Estimate' },
  { value: 'aerial_survey', label: 'Aerial Survey' },
];

// DCIC-03 Market Price Observation's data_source ENUM — "FMLD-native (not
// static NAITS)" per the field dictionary, so NAITS/NBS must never appear
// here even as a fallback.
const DEFAULT_DATA_SOURCES = [
  { value: 'FMLD Field Officer', label: 'FMLD Field Officer' },
  { value: 'State ADP', label: 'State ADP' },
  { value: 'LGA Report', label: 'LGA Report' },
  { value: 'NAQS', label: 'NAQS' },
  { value: 'Other', label: 'Other' },
];

const DEFAULT_VALIDATION_STATUSES = [
  { value: 'draft', label: 'Draft' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'validated', label: 'Validated' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
];

const DEFAULT_PRODUCTION_TYPES = [
  { value: 'milk', label: 'Milk' },
  { value: 'eggs', label: 'Eggs' },
  { value: 'liveweight', label: 'Liveweight' },
  { value: 'carcass', label: 'Carcass' },
  { value: 'hides', label: 'Hides' },
  { value: 'wool', label: 'Wool' },
  { value: 'honey', label: 'Honey' },
  { value: 'beeswax', label: 'Beeswax' },
];

function unwrapItems(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.results)) return payload.results;
  if (Array.isArray(payload?.data?.items)) return payload.data.items;
  if (Array.isArray(payload?.data?.results)) return payload.data.results;
  return [];
}

function normalizeOptionList(items, fallback = []) {
  const result = [...fallback, ...unwrapItems(items)]
    .map((item) => {
      if (item === null || item === undefined) return null;
      if (typeof item === 'string' || typeof item === 'number') {
        const text = String(item).trim();
        return text ? { value: text, label: text } : null;
      }

      const value = String(
        item.value ??
        item.code ??
        item.slug ??
        item.name ??
        item.label ??
        ''
      ).trim();
      if (!value) return null;

      const label = String(item.label ?? item.name ?? item.title ?? value).trim() || value;
      return { value, label };
    })
    .filter(Boolean);

  const deduped = new Map();
  result.forEach((option) => {
    if (!deduped.has(option.value)) deduped.set(option.value, option);
  });
  return Array.from(deduped.values());
}

async function safeList(request, fallback = []) {
  try {
    const result = await request();
    const items = unwrapItems(result);
    return items.length > 0 ? items : fallback;
  } catch {
    return fallback;
  }
}

async function fetchEnum(path, fallback = []) {
  try {
    const { data } = await client.get(path, { params: { per_page: 500 } });
    return normalizeOptionList(data, fallback);
  } catch {
    return fallback;
  }
}

export const referenceApi = {
  species: (params) => safeList(() => speciesApi.listAll(params)),
  breeds: (params) => safeList(() => breedEcotypeApi.listAll(params)),
  productionPurposes: (params) => safeList(() => productionPurposeApi.listAll(params)),
  productionTypes: async () => DEFAULT_PRODUCTION_TYPES,
  ageClasses: (params) => safeList(() => ageClassApi.listAll(params)),
  states: async () => {
    const items = await safeList(() => geospatialApi.states(), NIGERIAN_STATES.map((state) => state.value));
    return items;
  },
  lgas: async (params = {}) => {
    const state = typeof params === 'string' ? params : params?.state;
    if (!state) return [];
    return safeList(() => geospatialApi.lgas(state), []);
  },
  wards: async (params = {}) => {
    const state = typeof params === 'string' ? params : params?.state;
    const lga = typeof params === 'object' ? params?.lga : null;
    if (!state || !lga) return [];
    return safeList(() => geospatialApi.wards(state, lga), []);
  },
  institutions: (params) => safeList(() => institutionApi.list(params)),
  dataSources: () => fetchEnum('/v1/data-sources', DEFAULT_DATA_SOURCES),
  productionSystems: () => fetchEnum('/v1/production-systems', DEFAULT_PRODUCTION_SYSTEMS),
  countMethods: () => fetchEnum('/v1/count-methods', DEFAULT_COUNT_METHODS),
  validationStatuses: () => fetchEnum('/v1/validation-statuses', DEFAULT_VALIDATION_STATUSES),
  facilityTypes: () => facilityLookupsApi.facilityTypes().catch(() => [
    { value: 'veterinary_clinic', label: 'Veterinary Clinic' },
    { value: 'veterinary_hospital', label: 'Veterinary Hospital' },
    { value: 'feed_mill', label: 'Feed Mill' },
    { value: 'abattoir', label: 'Abattoir' },
  ]),
};

export async function getLivestockReferences(params = {}) {
  const [species, states, lgas, wards, breeds, productionPurposes, productionTypes, ageClasses, productionSystems, countMethods, dataSources, institutions, validationStatuses] =
    await Promise.all([
      referenceApi.species(params),
      referenceApi.states(params),
      referenceApi.lgas(params),
      referenceApi.wards(params),
      referenceApi.breeds(params),
      referenceApi.productionPurposes(params),
      referenceApi.productionTypes(params),
      referenceApi.ageClasses(params),
      referenceApi.productionSystems(params),
      referenceApi.countMethods(params),
      referenceApi.dataSources(params),
      referenceApi.institutions(params),
      referenceApi.validationStatuses(params),
    ]);

  return {
    species,
    states,
    lgas,
    wards,
    breeds,
    productionPurposes,
    productionTypes,
    ageClasses,
    productionSystems,
    countMethods,
    dataSources,
    institutions,
    validationStatuses,
  };
}

export async function getConflictsReferences(params = {}) {
  const [states, validationStatuses] = await Promise.all([
    referenceApi.states(params),
    referenceApi.validationStatuses(params),
  ]);
  return { states, validationStatuses };
}

export async function getInfrastructureReferences(params = {}) {
  const [states, facilityTypes, validationStatuses] = await Promise.all([
    referenceApi.states(params),
    referenceApi.facilityTypes(params),
    referenceApi.validationStatuses(params),
  ]);
  return { states, facilityTypes, validationStatuses };
}
